// Licence côté PWA. Le jeton THOR-LIC-… est stocké en IndexedDB et envoyé au
// core à l'enrôlement (le core fait foi). On le vérifie aussi LOCALEMENT via
// WebCrypto (RSASSA-PKCS1-v1_5 / SHA-256) pour afficher un état immédiat —
// même schéma que app/license.py du core, donc un jeton signé par toi vérifie
// identiquement en Python et dans le navigateur.

import { getMeta, setMeta } from "../storage/db";

const LICENSE_KEY = "license_token";
const PREFIX = "THOR-LIC-";

// Clé PUBLIQUE de licence (corps base64 du PEM SPKI, identique au core).
const PUBLIC_KEY_B64 =
  "MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAmVfynMUdHxcPbCe3p3Kz" +
  "zd539YHgpEiFBV7uTsaUObX9SsoXkSl25HPgTiNFxdtk/5QzItDdvGqJyagK5XiB" +
  "6YGUBK5pIkDFGcWWbbu70kRP9SUQmfPu8v5OD+nTD0gUMjUqdroRNC61TDPy14be" +
  "FjHIaCRfhFiBkpjG5C+vfUxKVsl7qHg/XTwzmNN+rb61Ve6NGzHI//XhCHBamZzU" +
  "x838xA8b3tmwNGzTUWzq6C5VBtSch2+rElauF4YbxwxStefFr/ctP7WR16IMEqFJ" +
  "eQe6Ma/GF7VLshMxvxwDlesGVw2tB4iYMxoib+Pk+zWYxsinXVAJRIvw6uwjPZRD" +
  "CQIDAQAB";

export interface LicensePayload {
  license_id: string;
  licensee_name: string;
  issued_at: string;
  licensee_email?: string;
  updates_until?: string;
  fingerprint?: string;
  produit?: string;
}

// Cloisonnement produit : cette app n'accepte QUE les licences Diagnostic
// (board_doctor) — ou sans produit precise (compat). Un jeton d'un AUTRE
// produit THOR (atelier, battery_tool...) est REJETE.
const ACCEPTED_PRODUITS = ["board_doctor", "diagnostic"];

function b64ToBuf(b64: string): ArrayBuffer {
  const bin = atob(b64);
  const buf = new ArrayBuffer(bin.length);
  const out = new Uint8Array(buf);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return buf;
}

let _keyPromise: Promise<CryptoKey> | null = null;
function publicKey(): Promise<CryptoKey> {
  if (_keyPromise === null) {
    _keyPromise = crypto.subtle.importKey(
      "spki",
      b64ToBuf(PUBLIC_KEY_B64),
      { name: "RSASSA-PKCS1-v1_5", hash: "SHA-256" },
      false,
      ["verify"],
    );
  }
  return _keyPromise;
}

/** Vérifie le jeton localement et retourne sa charge utile, ou null. */
export async function verifyLicense(token: string): Promise<LicensePayload | null> {
  const key = token.replace(/\s+/g, "");
  if (!key.startsWith(PREFIX)) return null;
  const body = key.slice(PREFIX.length);
  const dot = body.indexOf(".");
  if (dot < 0) return null;
  const payloadB64 = body.slice(0, dot);
  const sigB64 = body.slice(dot + 1);
  try {
    const payloadBuf = b64ToBuf(payloadB64);
    const ok = await crypto.subtle.verify(
      "RSASSA-PKCS1-v1_5",
      await publicKey(),
      b64ToBuf(sigB64),
      payloadBuf,
    );
    if (!ok) return null;
    const data = JSON.parse(new TextDecoder().decode(payloadBuf)) as LicensePayload;
    if (!data.license_id || !data.licensee_name || !data.issued_at) return null;
    const produit = (data.produit || "").trim().toLowerCase();
    if (produit && !ACCEPTED_PRODUITS.includes(produit)) return null;
    return data;
  } catch {
    return null;
  }
}

export async function getLicenseToken(): Promise<string | null> {
  return getMeta(LICENSE_KEY);
}

export async function setLicenseToken(token: string): Promise<void> {
  await setMeta(LICENSE_KEY, token.trim());
}
